import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Segmented } from '@/components/ui/segmented';
import { StatusChip } from '@/components/ui/status-chip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Receipt, AlertTriangle, Search, Ban, IndianRupee, X, CheckCircle2, RotateCcw, Pill, Printer } from 'lucide-react';
import PrintDepositReceiptModal, { type DepositReceipt } from '@/components/Billing/PrintDepositReceiptModal';
import { cn } from '@/lib/utils';
import {
  getCharges, addCharge, cancelCharge, getAccount, collectDeposit, finalizeBill, reopenBill, setOrdersBilling,
  type AdmissionAccount, type BedChargeLine,
} from '@/lib/ipdApi';
import { getBillingServices, recordInvoicePayments, refundAdvance } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { formatDateTime } from '@/lib/formatDate';
import { bedLabel } from '@/components/IPD/admissionStatus';
import { format } from 'date-fns';

// ─── Add Charge Modal ─────────────────────────────────────────────────────────

// Must match backend ENUM: admission_charges.charge_type (schema-mysql.sql).
// No room_rent: bed charges come from the stay.
const CHARGE_TYPES = [
  'nursing','consultation','procedure',
  'medicine','investigation','diet','other',
] as const;

interface ChargeForm {
  charge_type: string;
  description: string;
  quantity: string;
  unit_price: string;
  charge_date: string;
}

/** Runs one submit at a time — a double click must not save twice */
function useSingleFlight() {
  const busy = useRef(false);
  return async (fn: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true;
    try { await fn(); } finally { busy.current = false; }
  };
}

function emptyCharge(): ChargeForm {
  return {
    charge_type: 'other',
    description: '',
    quantity: '1',
    unit_price: '',
    charge_date: format(new Date(), 'yyyy-MM-dd'),
  };
}

function Sel({ value, onChange, children }: {
  value: string; onChange: (v: string) => void; children: React.ReactNode;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="h-8 w-full rounded-md border border-field bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
      {children}
    </select>
  );
}

type UnbilledOrder = AdmissionAccount['unbilled_orders'][number];

interface AddChargeModalProps {
  admissionId: string;
  admission?: any;
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  /** Bill this medication order: the charge is linked to it */
  fromOrder?: UnbilledOrder | null;
}

// Maps billing service category -> IPD charge_type
const SERVICE_CATEGORY_TO_CHARGE_TYPE: Record<string, string> = {
  consultation:  'consultation',
  procedure:     'procedure',
  investigation: 'investigation',
  diagnostic:    'investigation',
  other:         'other',
};

function AddChargeModal({ admissionId, admission, open, onClose, onAdded, fromOrder }: AddChargeModalProps) {
  const { currentUser } = useAuth();
  const [form, setForm] = useState<ChargeForm>(emptyCharge());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [services, setServices] = useState<any[]>([]);
  const [serviceQuery, setServiceQuery] = useState('');
  const [selectedService, setSelectedService] = useState<any | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const once = useSingleFlight();

  useEffect(() => {
    if (!open) return;
    setForm(fromOrder
      ? { ...emptyCharge(), charge_type: 'medicine', description: `${fromOrder.drug_name} ${fromOrder.dose}`.trim() }
      : emptyCharge());
    setError('');
    setServiceQuery('');
    setSelectedService(null);
    setSearchFocused(false);
    if (currentUser?.clinic_id) {
      getBillingServices(currentUser.clinic_id, undefined, undefined, true)
        .then((data: any) => setServices((data ?? []).filter((s: any) => s.is_active)))
        .catch(() => setServices([]));
    }
  }, [open, currentUser?.clinic_id, fromOrder]);

  const set = (k: keyof ChargeForm, v: string) => setForm(f => ({ ...f, [k]: v }));

  const filteredServices = serviceQuery.trim()
    ? services.filter(s => s.name.toLowerCase().includes(serviceQuery.trim().toLowerCase()))
    : services.slice(0, 20);

  const handleSelectService = (svc: any) => {
    setSelectedService(svc);
    setForm(f => ({
      ...f,
      charge_type: (svc.ipd_charge_type !== 'room_rent' && svc.ipd_charge_type) || SERVICE_CATEGORY_TO_CHARGE_TYPE[svc.category] || 'other',
      description: svc.name,
      unit_price:  String(svc.price),
    }));
    setServiceQuery('');
  };

  const handleClearService = () => {
    setSelectedService(null);
    setForm(emptyCharge());
  };

  const handleSave = () => once(async () => {
    setError('');
    if (!form.description.trim()) { setError('Description is required'); return; }
    if (!form.unit_price || isNaN(parseFloat(form.unit_price))) { setError('Valid unit price is required'); return; }
    setSaving(true);
    try {
      await addCharge(admissionId, {
        charge_type: form.charge_type,
        description: form.description,
        quantity:    parseFloat(form.quantity) || 1,
        unit_price:  parseFloat(form.unit_price),
        charge_date: form.charge_date,
        source_type: selectedService ? 'catalog' : 'manual',
        order_id:    fromOrder?.id,
      });
      onAdded();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to add charge');
    } finally {
      setSaving(false);
    }
  });

  const total = (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);
  const chargeTypeLabel = (t: string) => t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md p-0 gap-0"
        // Enter in a box adds the charge, as in the other dialogs (not in a list or the service search's own Enter)
        onKeyDown={e => {
          const t = e.target as HTMLElement;
          if (e.key === 'Enter' && t.tagName === 'INPUT' && !t.dataset.noEnterSave && !e.defaultPrevented && !saving) { e.preventDefault(); handleSave(); }
        }}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">{fromOrder ? `Bill ${fromOrder.drug_name}` : 'Add charge'}</h2>
          {total > 0 && (
            <div className="bg-muted/60 rounded-md px-3 py-1.5 text-right">
              <p className="text-xs text-muted-foreground leading-none mb-1">Total</p>
              <p className="font-mono text-base font-bold text-foreground leading-none">₹{total.toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {/* Service picker */}
        {fromOrder ? (
          <p className="px-5 pt-4 pb-3 text-xs text-muted-foreground">
            Ordered {fromOrder.dose} · {fromOrder.route.toUpperCase()} · {fromOrder.frequency}. Enter the quantity and price given from your pharmacy.
          </p>
        ) : (
        <div className="px-5 pt-4 pb-3">
          {!selectedService ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" />
                <Input
                  value={serviceQuery}
                  onChange={e => setServiceQuery(e.target.value)}
                  data-no-enter-save="true"
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  placeholder="Search services from catalog…"
                  className="pl-9 h-9 text-sm bg-muted/30 border-border/60 focus:bg-background transition-colors"
                  autoFocus
                />
                {serviceQuery && (
                  <button onClick={() => setServiceQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                )}

                {(searchFocused || !!serviceQuery.trim()) && filteredServices.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border border-border bg-popover shadow-md overflow-hidden max-h-52 overflow-y-auto divide-y divide-border/40">
                    {filteredServices.map(svc => {
                      const typeKey = svc.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[svc.category] || 'other';
                      return (
                        <button key={svc.id} type="button" onClick={() => handleSelectService(svc)}
                          className="w-full flex items-stretch text-left transition-colors hover:bg-primary/5 group">
                          <div className="flex items-center justify-between px-3 py-2 flex-1 gap-3 min-w-0">
                            <span className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{svc.name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-2xs px-1.5 py-0.5 rounded font-medium bg-muted text-muted-foreground">
                                {chargeTypeLabel(typeKey)}
                              </span>
                              <span className="font-mono text-sm font-semibold text-foreground tabular-nums">
                                ₹{Number(svc.price).toLocaleString('en-IN')}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground pl-0.5">
                IPD services only
              </p>

              {filteredServices.length === 0 && serviceQuery.trim() && services.length > 0 && (
                <p className="text-xs text-muted-foreground py-1 pl-0.5">No matches — fill details below for a manual charge.</p>
              )}
              {services.length === 0 && (
                <p className="text-xs text-muted-foreground py-1 pl-0.5">No IPD services configured. Go to Settings → Billing to add some.</p>
              )}
            </div>
          ) : (
            <div className="flex items-stretch rounded-md border border-primary/25 bg-primary/5 overflow-hidden">
              <div className="flex items-center gap-2.5 px-3 py-2.5 flex-1 min-w-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate leading-none mb-1">{selectedService.name}</p>
                  <span className="text-2xs font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {chargeTypeLabel(selectedService.ipd_charge_type || SERVICE_CATEGORY_TO_CHARGE_TYPE[selectedService.category] || 'other')}
                  </span>
                </div>
                <button onClick={handleClearService}
                  className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors shrink-0"
                  title="Clear selection">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        <div className="mx-5 border-t border-dashed border-border/50" />

        {/* Form */}
        <div className="px-5 py-4 space-y-3">
          {!selectedService && (
            <>
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">Description *</label>
                <Input value={form.description} onChange={e => set('description', e.target.value)}
                  className="h-8 text-sm" placeholder="e.g. Wound dressing, ECG, Consultant fee" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-[13px] font-semibold text-label mb-1">Charge type</label>
                  <Sel value={form.charge_type} onChange={v => set('charge_type', v)}>
                    {CHARGE_TYPES.map(t => (
                      <option key={t} value={t}>{chargeTypeLabel(t)}</option>
                    ))}
                  </Sel>
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-label mb-1">Unit price ₹</label>
                  <Input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)}
                    className="h-8 text-sm font-mono" min="0" step="0.01" placeholder="0" />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">Qty</label>
              <Input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)}
                className="h-8 text-sm font-mono" min="0.5" step="0.5" />
            </div>
            {selectedService && (
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">Price ₹</label>
                <Input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)}
                  className="h-8 text-sm font-mono" min="0" step="0.01" />
              </div>
            )}
            <div className={selectedService ? '' : 'col-span-2'}>
              <label className="block text-[13px] font-semibold text-label mb-1">Date</label>
              <Input type="date" value={form.charge_date}
                min={admission?.admission_date ? format(new Date(admission.admission_date), 'yyyy-MM-dd') : undefined}
                max={format(admission?.discharge_date ? new Date(admission.discharge_date) : new Date(), 'yyyy-MM-dd')}
                onChange={e => set('charge_date', e.target.value)}
                className="h-8 text-sm" />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/30 rounded-b-lg flex items-center justify-between gap-3">
          <div className="flex-1 text-xs font-mono text-muted-foreground tabular-nums">
            {total > 0 && (
              <span>
                <span className="opacity-70">{parseFloat(form.quantity) || 0} × ₹{(parseFloat(form.unit_price) || 0).toLocaleString('en-IN')} = </span>
                <span className="font-bold text-foreground">₹{total.toLocaleString('en-IN')}</span>
              </span>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button size="sm" className="h-8 text-xs" onClick={handleSave}
              disabled={saving || (!form.description.trim() && !selectedService)}>
              {saving ? 'Adding…' : total > 0 ? `Add — ₹${total.toLocaleString('en-IN')}` : 'Add charge'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bill tab ────────────────────────────────────────────────────────────────
// Running bill while the admission's bill is open (charges + bed days so far, deposits held);
// once final the figures are the invoice's and changes need "Reopen bill".

const inr = (n: number) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
] as const;
type Mode = typeof MODES[number]['value'];
const MODE_LABEL: Record<string, string> = { cash: 'Cash', upi: 'UPI', card: 'Card', cheque: 'Cheque', netbanking: 'Net banking', advance: 'From deposit', other: 'Other' };

const label = 'block text-xs font-medium text-label mb-1';

function ErrorNote({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div role="alert" className="flex items-start gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{children}
    </div>
  );
}

/** One line of the money summary */
function Line({ name, value, strong, muted }: { name: React.ReactNode; value: React.ReactNode; strong?: boolean; muted?: boolean }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-4 text-sm', muted && 'text-muted-foreground', strong && 'font-semibold')}>
      <span>{name}</span>
      <span className="font-mono tabular-nums whitespace-nowrap">{value}</span>
    </div>
  );
}

interface BillTabProps {
  admissionId: string;
  admission?: any;
  canWrite: boolean;
  refreshKey?: number;
  onChargesChanged?: () => void;
}

export default function IPDChargesTab({ admissionId, admission, canWrite, refreshKey, onChargesChanged }: BillTabProps) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const canViewInvoice = can('billing:read');
  const canBill = can('billing:write');
  const [charges, setCharges] = useState<any[]>([]);
  const [account, setAccount] = useState<AdmissionAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<null | 'add' | 'deposit' | 'final' | 'reopen' | 'collect' | 'refund'>(null);
  // A deposit receipt to print — right after taking one, or from the list
  const [receipt, setReceipt] = useState<DepositReceipt | null>(null);
  const receiptFor = (d: { amount: number; mode: string; ref?: string | null; at?: string; refund?: boolean; receipt?: string | null }): DepositReceipt => ({
    ...(d.refund ? { refund: true, reason: d.ref } : { reference_number: d.ref }),
    receipt_number: d.receipt,
    patient_id: admission?.patient_id, clinic_id: admission?.clinic_id, doctor_id: admission?.admitting_doctor_id,
    amount: d.amount, payment_mode: d.mode, date: d.at, admission_number: admission?.admission_number,
  });
  const [cancelTarget, setCancelTarget] = useState<any>(null);
  const [billOrder, setBillOrder] = useState<UnbilledOrder | null>(null);
  const [notBilledIds, setNotBilledIds] = useState<string[] | null>(null);

  const load = useCallback(() => {
    return Promise.all([
      getCharges(admissionId, true).then(data => setCharges(data ?? [])),
      getAccount(admissionId).then(setAccount),
    ]).catch(console.error).finally(() => setLoading(false));
  }, [admissionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const changed = () => { load(); onChargesChanged?.(); };

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>;
  if (!account) return <div className="text-sm text-muted-foreground py-8 text-center">Couldn't load the bill.</div>;

  const final = account.bill_status === 'final';
  const reopened = !final && !!account.invoice;
  const canEditCharges = canWrite && !final;
  const sortedCharges = [...charges].sort((a, b) =>
    String(a.charge_date).localeCompare(String(b.charge_date)) || String(a.created_at).localeCompare(String(b.created_at)));
  const linesTotal = account.bed_total + account.charges_total;

  // Money in: deposits and payments on the bill (deposit used on the bill is already in deposits)
  const received = [
    ...account.deposits.map(d => ({ id: d.id, at: d.created_at, what: 'Deposit', mode: d.payment_mode, ref: d.reference_number, by: d.collected_by_name, amount: Number(d.amount), receipt: d.receipt_number })),
    ...account.payments.filter(p => p.payment_mode !== 'advance').map(p => ({ id: p.id, at: p.payment_date, what: 'Payment', mode: p.payment_mode, ref: p.reference_number, by: p.collected_by_name, amount: Number(p.amount) })),
    ...account.refunds.map(r => ({ id: r.id, at: r.created_at, what: 'Refund', mode: r.refund_mode, ref: r.reason, by: r.processed_by_name, amount: -Number(r.amount), receipt: r.receipt_number })),
  ].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  const advanceUsed = account.deposit_used;
  // Deposits and refunds get a print button; every row then keeps that slot so the amounts stay in one column
  const printable = (what: string) => what === 'Deposit' || what === 'Refund';
  const printSlot = received.some(r => printable(r.what));

  return (
    <div className="space-y-4">

      {/* Where the bill stands */}
      <section aria-label="Bill summary" className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-divider">
          <div className="flex flex-wrap items-center gap-2">
            {final ? (
              <StatusChip tone="success">Final bill{account.invoice ? ` · ${account.invoice.invoice_number}` : ''}</StatusChip>
            ) : reopened ? (
              <StatusChip tone="warning">Reopened · {account.invoice?.invoice_number}</StatusChip>
            ) : (
              <StatusChip tone="neutral">Running bill</StatusChip>
            )}
            {final && account.finalized_by_name && (
              <span className="text-xs text-muted-foreground">by {account.finalized_by_name}{account.finalized_at ? ` · ${formatDateTime(account.finalized_at)}` : ''}</span>
            )}
            {reopened && <span className="text-xs text-muted-foreground">Changes reach the invoice when you make the bill final again</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Once the bill is final, money goes on the bill (Collect), not into a deposit */}
            {canBill && !final && (
              <Button size="sm" variant="outline" onClick={() => setDialog('deposit')}>
                <IndianRupee className="w-3.5 h-3.5 mr-1" /> Collect deposit
              </Button>
            )}
            {final && canBill && account.to_collect > 0 && (
              <Button size="sm" onClick={() => setDialog('collect')}>Collect {inr(account.to_collect)}</Button>
            )}
            {can('billing:refund') && account.spare_advance > 0 && (final || admission?.status !== 'admitted') && (
              <Button size="sm" variant="outline" onClick={() => setDialog('refund')}>Refund {inr(account.spare_advance)}</Button>
            )}
            {account.invoice && canViewInvoice && (
              <Button size="sm" variant="outline" onClick={() => navigate(`/billing?invoice=${account.invoice!.id}`)}>
                <Receipt className="w-3.5 h-3.5 mr-1" /> Invoice
              </Button>
            )}
            {final && canBill && (
              <Button size="sm" variant="outline" onClick={() => setDialog('reopen')}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen bill
              </Button>
            )}
            {!final && canBill && (
              <Button size="sm" onClick={() => setDialog('final')} disabled={linesTotal <= 0}>Final bill</Button>
            )}
          </div>
        </div>

        {account.out_of_date && (
          <p className="flex items-start gap-2 px-4 py-2 text-xs bg-highlight text-highlight-foreground border-b border-divider">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            The stay has changed since the bill was made final (now {inr(account.live_total)} before discount). Reopen the bill and make it final again to update it.
          </p>
        )}

        <div className="grid gap-x-8 gap-y-1.5 px-4 py-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Line name="Charges" value={inr(account.gross_total)} />
            {account.discount_amount > 0 && (
              <Line name={<>Discount{account.discount_reason ? <span className="text-muted-foreground"> · {account.discount_reason}</span> : null}</>} value={`− ${inr(account.discount_amount)}`} />
            )}
            <Line name="Bill total" value={inr(account.bill_total)} strong />
          </div>
          <div className="space-y-1.5">
            {account.paid > 0 && <Line name="Paid on the bill" value={inr(account.paid)} />}
            {/* Not when the "left to refund" line below already shows the same amount */}
            {account.advance_available > 0 && !(account.to_collect <= 0 && account.advance_available === account.spare_advance && (final || admission?.status !== 'admitted')) && (
              <Line name="Deposit held" value={inr(account.advance_available)} />
            )}
            {account.to_collect > 0 ? (
              <div className="flex items-baseline justify-between gap-4 text-sm font-semibold">
                <span>{final ? 'Due' : 'Collect more'}</span>
                <span className="rounded bg-highlight px-1.5 font-mono tabular-nums text-highlight-foreground">{inr(account.to_collect)}</span>
              </div>
            ) : account.spare_advance > 0 && (final || admission?.status !== 'admitted') ? (
              <Line name="Deposit left to refund" value={inr(account.spare_advance)} strong />
            ) : final ? (
              <Line name="Settled" value={<CheckCircle2 className="inline w-4 h-4 text-success" aria-label="Settled" />} strong />
            ) : (
              <Line name="Deposit covers the bill so far" value={<CheckCircle2 className="inline w-4 h-4 text-success" aria-hidden />} muted />
            )}
          </div>
        </div>
      </section>

      {/* Bill lines */}
      <section aria-label="Bill lines" className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Charges</h3>
          {canEditCharges ? (
            <Button size="sm" onClick={() => setDialog('add')}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add charge
            </Button>
          ) : final && canWrite ? (
            <span className="text-xs text-muted-foreground">Bill is final — reopen it to change charges</span>
          ) : null}
        </div>
        {/* Phones: one row per line, no sideways scroll */}
        <ul className="sm:hidden rounded-lg border border-border bg-card divide-y divide-divider">
          {account.bed_lines.map(l => (
            <li key={lineKey(l)} className="px-3 py-2 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span>{lineTitle(l)}</span>
                <span className="font-mono tabular-nums whitespace-nowrap font-medium">{inr(l.total)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {lineSub(l)} · {format(new Date(`${l.from_date}T00:00:00`), 'dd MMM')}{l.to_date !== l.from_date ? ` – ${format(new Date(`${l.to_date}T00:00:00`), 'dd MMM')}` : ''} · {l.days} {l.days === 1 ? 'day' : 'days'} × {inr(l.rate)}
              </p>
            </li>
          ))}
          {sortedCharges.map(c => (
            <li key={c.id} className={cn('px-3 py-2 text-sm', c.is_cancelled && 'text-muted-foreground')}>
              <div className="flex items-baseline justify-between gap-3">
                <span className={cn(c.is_cancelled && 'line-through')}>{c.description}</span>
                <span className={cn('font-mono tabular-nums whitespace-nowrap font-medium', c.is_cancelled && 'line-through')}>{inr(c.total_price)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {c.is_cancelled
                    ? `Cancelled${c.cancel_reason ? ` — ${c.cancel_reason}` : ''}`
                    : `${format(new Date(c.charge_date), 'dd MMM')} · ${Number(c.quantity)} × ${inr(c.unit_price)}${c.added_by_name ? ` · ${c.added_by_name}` : ''}`}
                </p>
                {!c.is_cancelled && canEditCharges && (
                  <button onClick={() => setCancelTarget(c)} className="text-xs text-destructive hover:underline inline-flex items-center gap-1 touch-target" aria-label={`Cancel ${c.description}`}>
                    <Ban className="w-3.5 h-3.5" /> Cancel
                  </button>
                )}
              </div>
            </li>
          ))}
          {account.bed_lines.length === 0 && sortedCharges.length === 0 && (
            <li className="px-3 py-10 text-center text-sm text-muted-foreground">No charges yet.</li>
          )}
          <li className="flex items-baseline justify-between gap-3 px-3 min-h-10 py-2 bg-input text-sm font-semibold">
            <span>Total</span><span className="font-mono tabular-nums">{inr(linesTotal)}</span>
          </li>
        </ul>
        <div className="hidden sm:block rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="h-8 border-b border-border bg-input">
                {['Date', 'Item', 'Qty', 'Rate', 'Amount', 'By', ''].map(h => (
                  <th key={h} scope="col" className={cn('px-3 text-left text-2xs font-medium uppercase tracking-wide text-label whitespace-nowrap', ['Qty', 'Rate', 'Amount'].includes(h) && 'text-right')}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {account.bed_lines.map(l => (
                <tr key={lineKey(l)} className="h-10 border-b border-divider">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground tabular-nums">{format(new Date(`${l.from_date}T00:00:00`), 'dd MMM')}{l.to_date !== l.from_date ? ` – ${format(new Date(`${l.to_date}T00:00:00`), 'dd MMM')}` : ''}</td>
                  <td className="px-3 py-2 min-w-[180px]">
                    {lineTitle(l)}
                    <span className="block text-xs text-muted-foreground">{lineSub(l)}{!final && admission?.status === 'admitted' ? ' · added each night' : ''}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{l.days} {l.days === 1 ? 'day' : 'days'}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{inr(l.rate)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap font-medium">{inr(l.total)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">Auto</td>
                  <td />
                </tr>
              ))}
              {sortedCharges.map(c => (
                <tr key={c.id} className={cn('h-10 border-b border-divider last:border-0', c.is_cancelled && 'text-muted-foreground')}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground tabular-nums">{format(new Date(c.charge_date), 'dd MMM')}</td>
                  <td className="px-3 py-2 min-w-[180px]">
                    <span className={cn(c.is_cancelled && 'line-through')}>{c.description}</span>
                    <span className="block text-xs text-muted-foreground">
                      {c.is_cancelled
                        ? `Cancelled${c.cancelled_by_name ? ` by ${c.cancelled_by_name}` : ''}${c.cancel_reason ? ` — ${c.cancel_reason}` : ''}`
                        : c.charge_type.charAt(0).toUpperCase() + c.charge_type.slice(1).replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className={cn('px-3 py-2 text-right font-mono tabular-nums', c.is_cancelled && 'line-through')}>{Number(c.quantity)}</td>
                  <td className={cn('px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap', c.is_cancelled && 'line-through')}>{inr(c.unit_price)}</td>
                  <td className={cn('px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap font-medium', c.is_cancelled && 'line-through')}>{inr(c.total_price)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{c.added_by_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {!c.is_cancelled && canEditCharges && (
                      <button onClick={() => setCancelTarget(c)} className="text-xs text-destructive hover:underline inline-flex items-center gap-1 touch-target" aria-label={`Cancel ${c.description}`}>
                        <Ban className="w-3.5 h-3.5" /> Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {account.bed_lines.length === 0 && sortedCharges.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-muted-foreground">No charges yet.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="h-10 border-t border-border bg-input">
                <td colSpan={4} className="px-3 py-2 text-right text-2xs font-medium uppercase tracking-wide text-label">Total</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap font-semibold">{inr(linesTotal)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* Medicines ordered that are not on the bill yet */}
      {account.unbilled_orders.length > 0 && (canEditCharges || canBill) && (
        <section aria-label="Medicines not billed" className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Medicines not on the bill</h3>
              <p className="text-xs text-muted-foreground">Ordered for this patient. Bill each one, or mark it as not billed here.</p>
            </div>
            {canBill && account.unbilled_orders.length > 1 && (
              <Button size="sm" variant="outline" onClick={() => setNotBilledIds(account.unbilled_orders.map(o => o.id))}>
                None billed here
              </Button>
            )}
          </div>
          <ul className="rounded-lg border border-border bg-card divide-y divide-divider">
            {account.unbilled_orders.map(o => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 px-3 min-h-10 py-1.5 text-sm">
                <div className="min-w-0 flex items-start gap-2">
                  <Pill className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden />
                  <div className="min-w-0">
                    <span className="font-medium">{o.drug_name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {o.dose} · {o.route.toUpperCase()} · {o.frequency} · from {format(new Date(o.start_date), 'dd MMM')}{o.status !== 'active' ? ` · ${o.status}` : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {canBill && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setNotBilledIds([o.id])}>Not billed here</Button>
                  )}
                  {canEditCharges && (
                    <Button size="sm" variant="outline" onClick={() => setBillOrder(o)}>Add charge</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Money received */}
      {(received.length > 0 || advanceUsed > 0) && (
        <section aria-label="Money received" className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Money received</h3>
          <div className="rounded-lg border border-border bg-card divide-y divide-divider">
            {received.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-3 min-h-10 py-1.5 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{r.what}</span>
                  <span className="text-muted-foreground"> · {MODE_LABEL[r.mode] ?? r.mode}{r.ref ? ` · ${r.ref}` : ''}</span>
                  <span className="block text-xs text-muted-foreground">{formatDateTime(r.at)}{r.by ? ` · ${r.by}` : ''}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className={cn('font-mono tabular-nums whitespace-nowrap', r.amount < 0 && 'text-destructive')}>{r.amount < 0 ? `− ${inr(-r.amount)}` : inr(r.amount)}</span>
                  {printable(r.what) ? (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={`Print ${r.what.toLowerCase()} receipt`} title="Print receipt"
                      onClick={() => setReceipt(receiptFor({ amount: Math.abs(r.amount), mode: r.mode, ref: r.ref, at: r.at, refund: r.what === 'Refund', receipt: (r as { receipt?: string | null }).receipt }))}>
                      <Printer className="w-4 h-4" />
                    </Button>
                  ) : printSlot && <span className="w-8" aria-hidden />}
                </div>
              </div>
            ))}
            {advanceUsed > 0 && (
              <div className="flex items-center justify-between gap-3 px-3 min-h-10 py-1.5 text-sm text-muted-foreground">
                <span>Deposit used on the bill</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="font-mono tabular-nums whitespace-nowrap">{inr(advanceUsed)}</span>
                  {printSlot && <span className="w-8" aria-hidden />}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <AddChargeModal admissionId={admissionId} admission={admission} open={dialog === 'add' || !!billOrder} fromOrder={billOrder}
        onClose={() => { setDialog(null); setBillOrder(null); }} onAdded={changed} />
      <NotBilledDialog admissionId={admissionId} orderIds={notBilledIds} onClose={() => setNotBilledIds(null)} onDone={changed} />
      <CancelChargeModal admissionId={admissionId} charge={cancelTarget} open={!!cancelTarget} onClose={() => setCancelTarget(null)} onCancelled={changed} />
      <DepositDialog admissionId={admissionId} open={dialog === 'deposit'} onClose={() => setDialog(null)}
        onDone={(d, print) => { changed(); if (print) setReceipt(receiptFor(d)); }} />
      <PrintDepositReceiptModal open={!!receipt} deposit={receipt} onClose={() => setReceipt(null)} />
      <FinalBillDialog admissionId={admissionId} account={account} open={dialog === 'final'} onClose={() => setDialog(null)} onDone={changed} />
      <ReopenDialog admissionId={admissionId} open={dialog === 'reopen'} onClose={() => setDialog(null)} onDone={changed} />
      {account.invoice && (
        <CollectDialog invoiceId={account.invoice.id} due={account.to_collect} open={dialog === 'collect'} onClose={() => setDialog(null)} onDone={changed} />
      )}
      <RefundDialog admission={admission} spare={account.spare_advance} open={dialog === 'refund'} onClose={() => setDialog(null)} onDone={changed} />
    </div>
  );
}

// ─── Shared money form: amount + mode + reference ─────────────────────────────

function useMoneyForm(open: boolean, initial: number) {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<Mode>('cash');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const once = useSingleFlight();
  useEffect(() => {
    if (!open) return;
    setAmount(initial > 0 ? String(initial) : '');
    setMode('cash');
    setReference('');
    setError('');
  }, [open, initial]);
  return { amount, setAmount, mode, setMode, reference, setReference, saving, setSaving, error, setError, once };
}

function MoneyFields({ f, amountLabel, max, onEnter }: { f: ReturnType<typeof useMoneyForm>; amountLabel: string; max?: number; onEnter: () => void }) {
  // Enter in the amount or reference saves, as in the other dialogs
  const enterSaves = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } };
  return (
    <>
      <div>
        <label className={label} htmlFor="money-amount">{amountLabel}</label>
        <Input id="money-amount" type="number" inputMode="decimal" min="0" step="0.01" max={max}
          value={f.amount} onChange={e => f.setAmount(e.target.value)} onKeyDown={enterSaves} className="font-mono" autoFocus />
      </div>
      <div>
        <span className={label}>Mode</span>
        <Segmented aria-label="Payment mode" options={MODES.map(m => ({ value: m.value, label: m.label }))} value={f.mode}
          onChange={v => { f.setMode(v); f.setReference(''); }} className="w-full" />
      </div>
      {f.mode !== 'cash' && (
        <div>
          <label className={label} htmlFor="money-ref">{f.mode === 'cheque' ? 'Cheque number' : f.mode === 'upi' ? 'UPI transaction ID' : 'Transaction ID'}</label>
          <Input id="money-ref" value={f.reference} onChange={e => f.setReference(e.target.value)} onKeyDown={enterSaves} />
        </div>
      )}
    </>
  );
}

const parseAmount = (s: string) => { const n = parseFloat(s); return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN; };

// ─── Collect deposit ─────────────────────────────────────────────────────────

function DepositDialog({ admissionId, open, onClose, onDone }: {
  admissionId: string; open: boolean; onClose: () => void;
  /** With what was taken; `print` when saved with "Save & print" */
  onDone: (d: { amount: number; mode: string; ref?: string; receipt?: string | null }, print: boolean) => void;
}) {
  const f = useMoneyForm(open, 0);
  const submit = (print = false) => f.once(async () => {
    const amt = parseAmount(f.amount);
    if (!(amt > 0)) { f.setError('Enter the amount'); return; }
    f.setSaving(true); f.setError('');
    try {
      const done = await collectDeposit(admissionId, { amount: amt, payment_mode: f.mode, reference_number: f.reference.trim() || undefined });
      onDone({ amount: amt, mode: f.mode, ref: f.reference.trim() || undefined, receipt: done?.deposit?.receipt_number }, print); onClose();
    } catch (e: any) { f.setError(e?.message || 'Could not save the deposit'); }
    finally { f.setSaving(false); }
  });
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Collect deposit</DialogTitle>
          <DialogDescription>Held as the patient's advance and used on the final bill.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <MoneyFields f={f} amountLabel="Amount (₹)" onEnter={() => submit(true)} />
          <ErrorNote>{f.error}</ErrorNote>
        </div>
        {/* Save & print first: the family is at the counter and wants the receipt */}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={f.saving}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={f.saving}>Save</Button>
          <Button onClick={() => submit(true)} disabled={f.saving}>{f.saving ? 'Saving…' : 'Save & print'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Final bill ──────────────────────────────────────────────────────────────

function FinalBillDialog({ admissionId, account, open, onClose, onDone }: {
  admissionId: string; account: AdmissionAccount; open: boolean; onClose: () => void; onDone: () => void;
}) {
  const { can } = usePermission();
  const canDiscount = can('billing:discount');
  const [discType, setDiscType] = useState<'flat' | 'percent'>('flat');
  const [discValue, setDiscValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const once = useSingleFlight();

  useEffect(() => {
    if (!open) return;
    setDiscType('flat');
    setDiscValue(account.discount_amount > 0 ? String(account.discount_amount) : '');
    setReason(account.discount_reason ?? '');
    setError('');
  }, [open, account.discount_amount, account.discount_reason]);

  const subtotal = account.live_total;
  const v = parseFloat(discValue) || 0;
  const discount = canDiscount
    ? Math.round((discType === 'percent' ? subtotal * v / 100 : v) * 100) / 100
    : account.discount_amount;
  const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);
  const stillDue = Math.max(0, total - account.paid);
  // Bill below what's paid (after a reopen): the deposit used on it goes back to the deposit
  const backToDeposit = Math.max(0, Math.round((account.paid - total) * 100) / 100);
  const depositHeld = account.advance_available + backToDeposit;
  const fromDeposit = Math.min(depositHeld, stillDue);
  const toCollect = Math.round((stillDue - fromDeposit) * 100) / 100;
  const left = Math.round((depositHeld - fromDeposit) * 100) / 100;

  const submit = () => once(async () => {
    setError('');
    if (discount > subtotal) { setError('Discount is more than the bill'); return; }
    if (canDiscount && discount > 0 && !reason.trim()) { setError('Give a reason for the discount'); return; }
    setSaving(true);
    try {
      await finalizeBill(admissionId, canDiscount
        ? { discount_amount: v, discount_type: discType, discount_reason: reason.trim() || undefined }
        : {});
      onDone(); onClose();
    } catch (e: any) { setError(e?.message || 'Could not make the bill final'); }
    finally { setSaving(false); }
  });

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Final bill</DialogTitle>
          <DialogDescription>
            {account.invoice ? `Updates invoice ${account.invoice.invoice_number}.` : 'Creates the invoice.'} Charges are locked until the bill is reopened.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {account.unbilled_orders.length > 0 && (
            <p className="flex items-start gap-2 text-xs bg-highlight text-highlight-foreground rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {account.unbilled_orders.length === 1 ? '1 ordered medicine is' : `${account.unbilled_orders.length} ordered medicines are`} not on the bill
              ({account.unbilled_orders.map(o => o.drug_name).join(', ')}). Bill them or mark them as not billed here first, unless they're meant to be left off.
            </p>
          )}
          <div className="space-y-1.5">
            {account.bed_total > 0 && <Line name="Bed & ward charges" value={inr(account.bed_total)} muted />}
            {account.charges_total > 0 && <Line name="Other charges" value={inr(account.charges_total)} muted />}
            <Line name="Charges" value={inr(subtotal)} />
          </div>

          {canDiscount ? (
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className={label} htmlFor="final-discount">Discount</label>
                  <Input id="final-discount" type="number" inputMode="decimal" min="0" step="0.01" placeholder="0"
                    value={discValue} onChange={e => setDiscValue(e.target.value)} className="font-mono" />
                </div>
                <Segmented<'flat' | 'percent'> aria-label="Discount type" value={discType} onChange={setDiscType} className="h-9"
                  options={[{ value: 'flat', label: '₹' }, { value: 'percent', label: '%' }]} />
              </div>
              {discount > 0 && (
                <div>
                  <label className={label} htmlFor="final-reason">Reason</label>
                  <Input id="final-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Doctor's concession" />
                </div>
              )}
            </div>
          ) : account.discount_amount > 0 ? (
            <Line name="Discount" value={`− ${inr(account.discount_amount)}`} />
          ) : null}

          <div className="space-y-1.5 border-t border-divider pt-3">
            {discount > 0 && <Line name="Discount" value={`− ${inr(discount)}`} muted />}
            <Line name="Bill total" value={inr(total)} strong />
            {account.paid > 0 && <Line name="Already paid" value={`− ${inr(Math.min(account.paid, total))}`} muted />}
            {backToDeposit > 0 && <Line name="Back to deposit" value={inr(backToDeposit)} muted />}
            {fromDeposit > 0 && <Line name="From deposit" value={`− ${inr(fromDeposit)}`} muted />}
            {toCollect > 0
              ? <Line name="To collect" value={inr(toCollect)} strong />
              : <Line name="Settled" value={inr(0)} strong />}
            {left > 0 && <p className="text-xs text-muted-foreground">{inr(left)} of the deposit is left over — refund it or keep it as advance.</p>}
          </div>
          <ErrorNote>{error}</ErrorNote>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Make bill final'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Stay lines (bed or ward daily charge) ───────────────────────────────────

const lineKey = (l: BedChargeLine) => `${l.kind}-${l.label}-${l.from_date}-${l.bed_number}`;
const lineTitle = (l: BedChargeLine) => (l.kind === 'bed' ? `${l.ward_name} · ${bedLabel(l.bed_number)}` : l.label);
const lineSub = (l: BedChargeLine) => (l.kind === 'bed' ? 'Bed charges' : `Daily · ${l.ward_name}`);

// ─── Medicines not billed here ───────────────────────────────────────────────

const NOT_BILLED_REASONS = ["Patient's own medicine", 'Bought from outside pharmacy', 'Included in package'];

function NotBilledDialog({ admissionId, orderIds, onClose, onDone }: { admissionId: string; orderIds: string[] | null; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState(NOT_BILLED_REASONS[0]);
  const [other, setOther] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const once = useSingleFlight();
  useEffect(() => { if (orderIds) { setReason(NOT_BILLED_REASONS[0]); setOther(''); setError(''); } }, [orderIds]);
  const why = reason === 'other' ? other.trim() : reason;
  const submit = () => once(async () => {
    if (!orderIds) return;
    if (!why) { setError('Say why'); return; }
    setSaving(true); setError('');
    try { await setOrdersBilling(admissionId, orderIds, why); onDone(); onClose(); }
    catch (e: any) { setError(e?.message || 'Could not save'); }
    finally { setSaving(false); }
  });
  return (
    <Dialog open={!!orderIds} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Not billed here</DialogTitle>
          <DialogDescription>
            {orderIds && orderIds.length > 1 ? `These ${orderIds.length} medicines` : 'This medicine'} won't be asked about again on this bill.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2" role="radiogroup" aria-label="Reason">
          {[...NOT_BILLED_REASONS, 'other'].map(r => (
            <label key={r} className="flex items-center gap-2 text-sm min-h-8">
              <input type="radio" name="not-billed-reason" checked={reason === r} onChange={() => setReason(r)} />
              {r === 'other' ? 'Other' : r}
            </label>
          ))}
          {reason === 'other' && (
            <Input aria-label="Reason" value={other} onChange={e => setOther(e.target.value)} maxLength={100} placeholder="Why it isn't billed" autoFocus />
          )}
          <ErrorNote>{error}</ErrorNote>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reopen bill ─────────────────────────────────────────────────────────────

function ReopenDialog({ admissionId, open, onClose, onDone }: { admissionId: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const once = useSingleFlight();
  useEffect(() => { if (open) { setReason(''); setError(''); } }, [open]);
  const submit = () => once(async () => {
    if (!reason.trim()) { setError('Give a reason'); return; }
    setSaving(true); setError('');
    try { await reopenBill(admissionId, reason.trim()); onDone(); onClose(); }
    catch (e: any) { setError(e?.message || 'Could not reopen the bill'); }
    finally { setSaving(false); }
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Reopen bill</DialogTitle>
          <DialogDescription>Charges can be changed again. The invoice keeps its number and payments, and is updated when you make the bill final.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={label} htmlFor="reopen-reason">Reason</label>
            <Input id="reopen-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Lab charge missed" autoFocus />
          </div>
          <ErrorNote>{error}</ErrorNote>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Reopening…' : 'Reopen bill'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Collect payment on the final bill ───────────────────────────────────────

function CollectDialog({ invoiceId, due, open, onClose, onDone }: { invoiceId: string; due: number; open: boolean; onClose: () => void; onDone: () => void }) {
  const f = useMoneyForm(open, due);
  const submit = () => f.once(async () => {
    const amt = parseAmount(f.amount);
    if (!(amt > 0)) { f.setError('Enter the amount'); return; }
    if (amt > due + 0.005) { f.setError(`Only ${inr(due)} is due`); return; }
    f.setSaving(true); f.setError('');
    try {
      await recordInvoicePayments(invoiceId, [{ amount: amt, payment_mode: f.mode, reference_number: f.reference.trim() || undefined }]);
      onDone(); onClose();
    } catch (e: any) { f.setError(e?.message || 'Could not save the payment'); }
    finally { f.setSaving(false); }
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Collect payment</DialogTitle>
          <DialogDescription>{inr(due)} due on the final bill.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <MoneyFields f={f} amountLabel="Amount (₹)" max={due} onEnter={submit} />
          <ErrorNote>{f.error}</ErrorNote>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={f.saving}>Cancel</Button>
          <Button onClick={submit} disabled={f.saving}>{f.saving ? 'Saving…' : 'Save payment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Refund the deposit left over ────────────────────────────────────────────
// Also used outside IPD (no admission) to give back an advance the patient no longer needs.

export function RefundDialog({ admission, patientId, clinicId, doctorId, spare, open, onClose, onDone }: {
  admission?: any; patientId?: string; clinicId?: string;
  /** Whose letterhead the refund receipt prints on (the admitting doctor for a stay) */
  doctorId?: string;
  spare: number; open: boolean; onClose: () => void; onDone: () => void;
}) {
  const f = useMoneyForm(open, spare);
  // The receipt the patient signs for the money handed back
  const [receipt, setReceipt] = useState<DepositReceipt | null>(null);
  const reason = admission ? 'Deposit left over after IPD bill' : 'Advance not needed';
  const submit = (print = false) => f.once(async () => {
    const amt = parseAmount(f.amount);
    if (!(amt > 0)) { f.setError('Enter the amount'); return; }
    if (amt > spare + 0.005) { f.setError(`Only ${inr(spare)} is left`); return; }
    f.setSaving(true); f.setError('');
    try {
      await refundAdvance(admission?.patient_id ?? patientId!, admission?.clinic_id ?? clinicId!, {
        amount: amt, refund_mode: f.mode, admission_id: admission?.id,
        reference_number: f.reference.trim() || undefined,
        reason,
      });
      onDone(); onClose();
      if (print) setReceipt({
        refund: true, reason, amount: amt, payment_mode: f.mode, reference_number: f.reference.trim() || undefined,
        patient_id: admission?.patient_id ?? patientId!, clinic_id: admission?.clinic_id ?? clinicId!,
        doctor_id: admission?.admitting_doctor_id ?? doctorId, admission_number: admission?.admission_number,
      });
    } catch (e: any) { f.setError(e?.message || 'Could not save the refund'); }
    finally { f.setSaving(false); }
  });
  return (
    <>
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{admission ? 'Refund deposit' : 'Refund advance'}</DialogTitle>
          <DialogDescription>
            {admission
              ? `${inr(spare)} of the patient's deposit is not needed for the bill. Whatever you don't refund stays as their advance.`
              : `The patient has ${inr(spare)} of advance. Whatever you don't refund stays as their advance.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <MoneyFields f={f} amountLabel="Refund (₹)" max={spare} onEnter={() => submit(true)} />
          <ErrorNote>{f.error}</ErrorNote>
        </div>
        {/* Save & print first: the patient signs the receipt for the money handed back */}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={f.saving}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={f.saving}>Save</Button>
          <Button onClick={() => submit(true)} disabled={f.saving}>{f.saving ? 'Saving…' : 'Save & print'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <PrintDepositReceiptModal open={!!receipt} deposit={receipt} onClose={() => setReceipt(null)} />
    </>
  );
}

// ─── Cancel charge ───────────────────────────────────────────────────────────

function CancelChargeModal({ admissionId, charge, open, onClose, onCancelled }: {
  admissionId: string; charge: any; open: boolean; onClose: () => void; onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const once = useSingleFlight();
  useEffect(() => { if (open) { setReason(''); setError(''); } }, [open]);
  const submit = () => once(async () => {
    if (!reason.trim()) { setError('Give a reason'); return; }
    setSaving(true); setError('');
    try { await cancelCharge(admissionId, charge.id, reason.trim()); onCancelled(); onClose(); }
    catch (e: any) { setError(e?.message || 'Could not cancel the charge'); }
    finally { setSaving(false); }
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Cancel charge</DialogTitle>
          <DialogDescription>{charge?.description}{charge ? ` · ${inr(charge.total_price)}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={label} htmlFor="cancel-reason">Reason</label>
            <Input id="cancel-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Not given" autoFocus />
          </div>
          <ErrorNote>{error}</ErrorNote>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Back</Button>
          <Button variant="destructive" onClick={submit} disabled={saving}>{saving ? 'Cancelling…' : 'Cancel charge'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
